import { statusColour } from './orgChartApi';
import {
  SEATS_STATUS_FILTER, ACTING_STATUS_FILTER, OTHER_STATUS_FILTER, PEOPLE_STATUS_FILTER,
} from './statusFilters';

// People first, positions second. The chart draws one card per person
// AND one per open seat, so a single "positions" figure mixed the two
// and never said how many people work here. The headline splits them;
// the total sits underneath as their sum, and the status tiles below
// break down Employees only.
//
// Every figure is derived by the page from the VISIBLE (already redacted) node
// set and passed in as `counts`; this component never counts anything itself.
// Each control writes the same `filters.status` the Status <select> reads, via
// `onSelect`, which toggles back to 'all' when the active value is chosen again.
export default function OrgStatsStrip({ counts, status, isAdmin, onSelect }) {
  // The Employees breakdown. Each tile's value is what a click sets
  // `status` to. Open positions and the total are NOT tiles: they sit
  // in the headline and the total line, so these five sum to Employees.
  const tiles = [
    { label: 'Active', n: counts.active, value: 'Active' },
    { label: 'Probation', n: counts.probation, value: 'Probation' },
    { label: 'On Notice', n: counts.onNotice, value: 'On Notice' },
    { label: 'PIP', n: counts.pip, value: 'PIP' },
    // Only when it has members: on data where every filled position carries a
    // named status this is 0 and an always-present "Other 0" would be noise.
    ...(counts.other > 0
      ? [{ label: 'Other', n: counts.other, value: OTHER_STATUS_FILTER }]
      : []),
  ];

  const totalSelected = status === 'all';
  const employeesSelected = status === PEOPLE_STATUS_FILTER;
  const openSelected = status === SEATS_STATUS_FILTER;
  const actingSelected = status === ACTING_STATUS_FILTER;

  return (
    <div className="oc-stats-wrap">
      <div className="oc-stat-head">
        <button
          type="button"
          className={`oc-stat-head-item oc-stat-employees ${employeesSelected ? 'oc-stat-head-item--selected' : ''}`}
          aria-pressed={employeesSelected}
          onClick={() => onSelect(PEOPLE_STATUS_FILTER)}
        >
          <span className="oc-stat-head-n">{counts.employees}</span>
          <span className="oc-stat-head-l">Employees</span>
        </button>

        {/* Acting is a SUBSET of open positions, so it lives inside this
            item rather than beside it. Nested interactive content is
            invalid inside a <button>, so the item is a div with two real
            buttons in it. */}
        <div className={`oc-stat-head-item oc-stat-open ${openSelected ? 'oc-stat-head-item--selected' : ''}`}>
          <button
            type="button"
            className="oc-stat-head-main"
            aria-pressed={openSelected}
            onClick={() => onSelect(SEATS_STATUS_FILTER)}
          >
            <span className="oc-stat-head-n">{counts.seats}</span>
            <span className="oc-stat-head-l">Open positions</span>
          </button>
          {/* Only when some vacancy is actually covered: a permanent
              "0 acting" is noise on an org with no acting cover. */}
          {counts.acting > 0 && (
            <button
              type="button"
              className={`oc-stat-sub ${actingSelected ? 'oc-stat-sub--selected' : ''}`}
              aria-pressed={actingSelected}
              onClick={() => onSelect(ACTING_STATUS_FILTER)}
            >
              {counts.acting} acting
            </button>
          )}
        </div>
      </div>

      <div className="oc-stats">
        {tiles.map(({ label, n, value }) => {
          const selected = status === value;
          const colour = statusColour(label);
          const cls = ['oc-stat', 'oc-stat--status', selected ? 'oc-stat--selected' : '']
            .filter(Boolean).join(' ');
          const suffix = !isAdmin && (label === 'Probation' || label === 'On Notice' || label === 'PIP')
            ? ' in your departments' : '';
          return (
            <button
              type="button"
              key={label}
              className={cls}
              style={{ borderLeftColor: colour }}
              aria-pressed={selected}
              onClick={() => onSelect(value)}
            >
              <span className="oc-stat-n" style={{ color: colour }}>{n}</span>
              <span className="oc-stat-l">{label}{suffix}</span>
            </button>
          );
        })}
      </div>

      {/* The sum of the two headline figures, kept for the reader planning
          seats. Selecting it clears the status filter. */}
      <button
        type="button"
        className={`oc-stat-total ${totalSelected ? 'oc-stat-total--selected' : ''}`}
        aria-pressed={totalSelected}
        onClick={() => onSelect('all')}
      >
        <span className="oc-stat-total-n">{counts.total}</span>
        <span className="oc-stat-total-l">Total positions</span>
      </button>
    </div>
  );
}
